import { useCallback, useMemo, useRef, useState } from 'react';

import ContentEditor from '@sayyesschool/frontend/components/content-editor';
import { useFormData } from '@sayyesschool/frontend/hooks/form';
import { Button, Flex, Form, IconButton, Radio, Surface, Text } from '@sayyesschool/frontend/ui-components';

import {
    BLANK_PLACEHOLDER,
    contentToFormValue,
    formValueToContent,
    getBlankPlaceholderCount,
    hasBlankPlaceholder
} from './helpers';

const MIN_OPTIONS_COUNT = 2;

function getFormData(question = {}) {
    const options = question.options?.length
        ? question.options
        : Array.from({ length: 4 }, () => ({
            content: '',
            correct: false
        }));

    return {
        topic: question.topic || '',
        level: question.level || 1,
        content: contentToFormValue(question.content || ''),
        options: options.map(option => ({
            content: option.content || '',
            correct: Boolean(option.correct)
        }))
    };
}

export default function TestQuestionForm({
    question,
    onSubmit,
    ...props
}) {
    const { data, setData, handleChange } = useFormData(
        getFormData(question),
        [question?.id]
    );

    const editorRef = useRef();
    const [error, setError] = useState();

    const correctOptionIndex = useMemo(
        () => data.options.findIndex(option => option.correct),
        [data.options]
    );

    const handleOptionChange = useCallback((index, value) => {
        setData(data => ({
            ...data,
            options: data.options.map((option, optionIndex) => optionIndex === index ? {
                ...option,
                content: value
            } : option)
        }));
    }, [setData]);

    const handleCorrectChange = useCallback(index => {
        setData(data => ({
            ...data,
            options: data.options.map((option, optionIndex) => ({
                ...option,
                correct: optionIndex === index
            }))
        }));

        setError();
    }, [setData]);

    const handleAddOption = useCallback(() => {
        setData(data => ({
            ...data,
            options: [
                ...data.options,
                {
                    content: '',
                    correct: false
                }
            ]
        }));
    }, [setData]);

    const handleDeleteOption = useCallback(index => {
        setData(data => {
            if (data.options.length <= MIN_OPTIONS_COUNT) {
                return data;
            }

            const options = data.options.filter((_, optionIndex) => optionIndex !== index);

            return {
                ...data,
                options: options.some(option => option.correct)
                    ? options
                    : options.map((option, optionIndex) => ({
                        ...option,
                        correct: optionIndex === 0
                    }))
            };
        });
    }, [setData]);

    const handleSubmit = useCallback(() => {
        const formContent = editorRef.current?.getData();
        const options = data.options.map(option => ({
            ...option,
            content: option.content.trim()
        }));

        if (!formContent || formContent === '<p></p>') {
            setError('Добавьте текст вопроса.');

            return;
        }

        if (!hasBlankPlaceholder(formContent)) {
            setError(`Добавьте ${BLANK_PLACEHOLDER} в текст вопроса, чтобы отметить место для ответа.`);

            return;
        }

        if (getBlankPlaceholderCount(formContent) > 1) {
            setError(`В тексте вопроса должен быть ровно один ${BLANK_PLACEHOLDER}.`);

            return;
        }

        if (options.some(option => !option.content)) {
            setError('Заполните все варианты ответа.');

            return;
        }

        if (!options.some(option => option.correct)) {
            setError('Выберите правильный вариант ответа.');

            return;
        }

        setError();

        return onSubmit({
            ...data,
            level: Number(data.level) || 1,
            content: formValueToContent(formContent),
            options
        });
    }, [data, onSubmit]);

    return (
        <Form onSubmit={handleSubmit} {...props}>
            <Flex gap="m" column>
                <Form.Input
                    label="Тема"
                    name="topic"
                    value={data.topic}
                    required
                    onChange={handleChange}
                />

                <Form.Input
                    label="Уровень"
                    type="number"
                    min={1}
                    name="level"
                    step={1}
                    value={data.level}
                    required
                    onChange={handleChange}
                />

                <Form.Field label="Текст вопроса" message={`Используйте ${BLANK_PLACEHOLDER}, чтобы обозначить место для ответа.`}>
                    <Surface variant="outlined">
                        <ContentEditor
                            key={question?.id || 'new-question'}
                            ref={editorRef}
                            content={data.content}
                            simple
                        />
                    </Surface>
                </Form.Field>

                <Form.Field label="Варианты ответа">
                    <Flex gap="s" column>
                        {data.options.map((option, index) => (
                            <Flex
                                key={index}
                                gap="s"
                                align="center"
                            >
                                <Radio
                                    name="correctOption"
                                    checked={correctOptionIndex === index}
                                    onChange={() => handleCorrectChange(index)}
                                >
                                    Правильный
                                </Radio>

                                <Form.Input
                                    label={`Вариант ${index + 1}`}
                                    formFieldProps={{ sx: { 'flexGrow': 1 } }}
                                    value={option.content}
                                    required
                                    onChange={({ target }) => handleOptionChange(index, target.value)}
                                />

                                <IconButton
                                    icon="delete"
                                    title="Удалить вариант"
                                    variant="plain"
                                    color="danger"
                                    disabled={data.options.length <= MIN_OPTIONS_COUNT}
                                    onClick={() => handleDeleteOption(index)}
                                />
                            </Flex>
                        ))}

                        <Button
                            type="button"
                            icon="add"
                            content="Добавить вариант"
                            variant="outlined"
                            onClick={handleAddOption}
                        />
                    </Flex>
                </Form.Field>

                {error &&
                    <Text color="danger">{error}</Text>
                }
            </Flex>
        </Form>
    );
}